/**
 * Sistema de Monitoramento de Produção
 * Monitora performance, erros e métricas da aplicação em tempo real
 */

class ProductionMonitor {
  constructor() {
    this.isProduction = import.meta.env.MODE === 'production';
    this.metrics = {
      errors: [],
      performance: [],
      userActivity: [],
      resources: [],
      authentication: []
    };
    this.maxMetricsHistory = 100;
    this.reportInterval = 5 * 60 * 1000; // 5 minutos
    
    if (this.isProduction) {
      this.initialize();
    }
  }

  initialize() {
    this.setupErrorMonitoring();
    this.setupPerformanceMonitoring();
    this.setupResourceMonitoring();
    this.setupUserActivityMonitoring();
    this.setupAuthenticationMonitoring();
    this.startReporting();
    
    console.log('[ProductionMonitor] Sistema de monitoramento inicializado');
  }

  // Monitoramento de Erros
  setupErrorMonitoring() {
    // Captura erros JavaScript não tratados
    window.addEventListener('error', (event) => {
      this.logError({
        type: 'javascript_error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent
      });
    });

    // Captura promises rejeitadas não tratadas
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        type: 'promise_rejection',
        message: event.reason?.message || 'Unhandled promise rejection',
        stack: event.reason?.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    });

    // Intercepta console.error para capturar erros do React
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string') {
        this.logError({
          type: 'console_error',
          message: args[0],
          details: args.slice(1),
          timestamp: new Date().toISOString(),
          url: window.location.href
        });
      }
      originalConsoleError.apply(console, args);
    };
  }

  // Monitoramento de Performance
  setupPerformanceMonitoring() {
    // Web Vitals
    if ('web-vitals' in window) {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS(this.logWebVital.bind(this));
        getFID(this.logWebVital.bind(this));
        getFCP(this.logWebVital.bind(this));
        getLCP(this.logWebVital.bind(this));
        getTTFB(this.logWebVital.bind(this));
      });
    }

    // Monitorar tempo de carregamento das rotas
    this.setupRoutePerformance();
    
    // Monitorar componentes lentos
    this.setupComponentPerformance();
  }

  setupRoutePerformance() {
    let routeStartTime = performance.now();
    
    // Monitora mudanças de rota
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      window.productionMonitor?.logRouteChange(args[2], routeStartTime);
      routeStartTime = performance.now();
      return originalPushState.apply(window.history, args);
    };

    window.history.replaceState = function(...args) {
      window.productionMonitor?.logRouteChange(args[2], routeStartTime);
      routeStartTime = performance.now();
      return originalReplaceState.apply(window.history, args);
    };

    window.addEventListener('popstate', () => {
      this.logRouteChange(window.location.pathname, routeStartTime);
      routeStartTime = performance.now();
    });
  }

  setupComponentPerformance() {
    // Intercepta React DevTools se disponível
    if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
      const originalOnCommitFiberRoot = hook.onCommitFiberRoot;
      
      hook.onCommitFiberRoot = (id, root, priorityLevel) => {
        const renderTime = performance.now();
        this.logComponentRender(renderTime);
        return originalOnCommitFiberRoot?.call(hook, id, root, priorityLevel);
      };
    }
  }

  // Monitoramento de Recursos
  setupResourceMonitoring() {
    // Monitorar uso de memória
    setInterval(() => {
      if ('memory' in performance) {
        this.logResourceUsage({
          type: 'memory',
          used: performance.memory.usedJSHeapSize,
          total: performance.memory.totalJSHeapSize,
          limit: performance.memory.jsHeapSizeLimit,
          timestamp: new Date().toISOString()
        });
      }
    }, 30000); // A cada 30 segundos

    // Monitorar Network Information API
    if ('connection' in navigator) {
      const connection = navigator.connection;
      this.logResourceUsage({
        type: 'network',
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData,
        timestamp: new Date().toISOString()
      });

      connection.addEventListener('change', () => {
        this.logResourceUsage({
          type: 'network_change',
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          timestamp: new Date().toISOString()
        });
      });
    }
  }

  // Monitoramento de Atividade do Usuário
  setupUserActivityMonitoring() {
    let isActive = true;
    let lastActivity = Date.now();

    // Detectar inatividade
    const activities = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    activities.forEach(event => {
      document.addEventListener(event, () => {
        lastActivity = Date.now();
        if (!isActive) {
          isActive = true;
          this.logUserActivity('became_active');
        }
      }, true);
    });

    // Verificar inatividade a cada minuto
    setInterval(() => {
      const now = Date.now();
      if (now - lastActivity > 5 * 60 * 1000 && isActive) { // 5 minutos
        isActive = false;
        this.logUserActivity('became_inactive');
      }
    }, 60000);

    // Visibilidade da página
    document.addEventListener('visibilitychange', () => {
      this.logUserActivity(document.hidden ? 'page_hidden' : 'page_visible');
    });
  }

  // Monitoramento de Autenticação
  setupAuthenticationMonitoring() {
    // Intercepta mudanças no contexto de autenticação
    this.monitorAuthContext();
    
    // Monitora tentativas de login/logout
    this.monitorAuthActions();
  }

  // Métodos de Logging
  logError(error) {
    this.metrics.errors.push(error);
    this.trimMetrics('errors');
    
    // Log crítico imediato para erros graves
    if (this.isCriticalError(error)) {
      this.sendCriticalAlert(error);
    }
  }

  logWebVital(metric) {
    this.metrics.performance.push({
      type: 'web_vital',
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      timestamp: new Date().toISOString()
    });
    this.trimMetrics('performance');
  }

  logRouteChange(path, startTime) {
    const loadTime = performance.now() - startTime;
    this.metrics.performance.push({
      type: 'route_change',
      path: path,
      loadTime: loadTime,
      timestamp: new Date().toISOString()
    });
    this.trimMetrics('performance');
  }

  logComponentRender(renderTime) {
    this.metrics.performance.push({
      type: 'component_render',
      renderTime: renderTime,
      timestamp: new Date().toISOString()
    });
    this.trimMetrics('performance');
  }

  logResourceUsage(resource) {
    this.metrics.resources.push(resource);
    this.trimMetrics('resources');
  }

  logUserActivity(activity) {
    this.metrics.userActivity.push({
      type: activity,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });
    this.trimMetrics('userActivity');
  }

  logAuthEvent(event) {
    this.metrics.authentication.push({
      ...event,
      timestamp: new Date().toISOString()
    });
    this.trimMetrics('authentication');
  }

  // Utilitários
  trimMetrics(type) {
    if (this.metrics[type].length > this.maxMetricsHistory) {
      this.metrics[type] = this.metrics[type].slice(-this.maxMetricsHistory);
    }
  }

  isCriticalError(error) {
    const criticalPatterns = [
      /authentication/i,
      /payment/i,
      /security/i,
      /database/i,
      /network/i,
      /chunk.*failed/i
    ];
    
    return criticalPatterns.some(pattern => 
      pattern.test(error.message) || pattern.test(error.stack || '')
    );
  }

  async sendCriticalAlert(error) {
    try {
      // Enviar alerta para sistema de monitoramento
      const alert = {
        type: 'critical_error',
        error: error,
        timestamp: new Date().toISOString(),
        environment: 'production',
        version: import.meta.env.VITE_VERSION || 'unknown'
      };

      // Tentar enviar para endpoint de alertas
      if (typeof fetch !== 'undefined') {
        await fetch('/api/alerts/critical', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(alert)
        }).catch(() => {
          // Fallback: salvar no localStorage para envio posterior
          const alerts = JSON.parse(localStorage.getItem('pending_alerts') || '[]');
          alerts.push(alert);
          localStorage.setItem('pending_alerts', JSON.stringify(alerts.slice(-10)));
        });
      }
    } catch (err) {
      // Não fazer nada se o alerta falhar para não causar loops
    }
  }

  // Relatórios
  startReporting() {
    setInterval(() => {
      this.generateReport();
    }, this.reportInterval);

    // Relatório inicial após 1 minuto
    setTimeout(() => {
      this.generateReport();
    }, 60000);
  }

  generateReport() {
    const report = {
      timestamp: new Date().toISOString(),
      environment: 'production',
      version: import.meta.env.VITE_VERSION || 'unknown',
      url: window.location.href,
      userAgent: navigator.userAgent,
      metrics: {
        errors: this.metrics.errors.length,
        performance: this.getPerformanceSummary(),
        resources: this.getResourcesSummary(),
        userActivity: this.getUserActivitySummary(),
        authentication: this.metrics.authentication.length
      },
      details: this.metrics
    };

    // Enviar relatório
    this.sendReport(report);
  }

  getPerformanceSummary() {
    const perf = this.metrics.performance;
    const routes = perf.filter(p => p.type === 'route_change');
    const vitals = perf.filter(p => p.type === 'web_vital');

    return {
      avgRouteLoadTime: routes.length > 0 ? 
        routes.reduce((sum, r) => sum + r.loadTime, 0) / routes.length : 0,
      webVitals: vitals.reduce((acc, v) => {
        acc[v.name] = { value: v.value, rating: v.rating };
        return acc;
      }, {}),
      totalMeasurements: perf.length
    };
  }

  getResourcesSummary() {
    const resources = this.metrics.resources;
    const memory = resources.filter(r => r.type === 'memory');
    const network = resources.filter(r => r.type === 'network' || r.type === 'network_change');

    return {
      memoryUsage: memory.length > 0 ? memory[memory.length - 1] : null,
      networkInfo: network.length > 0 ? network[network.length - 1] : null,
      totalMeasurements: resources.length
    };
  }

  getUserActivitySummary() {
    const activity = this.metrics.userActivity;
    const activeTime = activity.filter(a => a.type === 'became_active').length;
    const inactiveTime = activity.filter(a => a.type === 'became_inactive').length;

    return {
      activeEvents: activeTime,
      inactiveEvents: inactiveTime,
      visibilityChanges: activity.filter(a => a.type.includes('page_')).length,
      totalEvents: activity.length
    };
  }

  async sendReport(report) {
    try {
      if (typeof fetch !== 'undefined') {
        await fetch('/api/monitoring/report', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(report)
        }).catch(() => {
          // Salvar no localStorage se o envio falhar
          const reports = JSON.parse(localStorage.getItem('pending_reports') || '[]');
          reports.push(report);
          localStorage.setItem('pending_reports', JSON.stringify(reports.slice(-5)));
        });
      }
    } catch (err) {
      // Silenciar erros de monitoramento
    }
  }

  // Métodos específicos de monitoramento
  monitorAuthContext() {
    // Será implementado para integrar com o AuthContext
    if (window.authContextMonitor) {
      window.authContextMonitor.addListener((event) => {
        this.logAuthEvent(event);
      });
    }
  }

  monitorAuthActions() {
    // Monitora ações de autenticação através de interceptação
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const url = args[0];
      const options = args[1] || {};

      // Detectar chamadas de autenticação
      if (typeof url === 'string' && (
        url.includes('/login') || 
        url.includes('/logout') || 
        url.includes('/auth') ||
        url.includes('/token')
      )) {
        const startTime = performance.now();
        
        try {
          const response = await originalFetch.apply(window, args);
          const endTime = performance.now();
          
          this.logAuthEvent({
            type: 'auth_request',
            url: url,
            method: options.method || 'GET',
            status: response.status,
            duration: endTime - startTime,
            success: response.ok
          });
          
          return response;
        } catch (error) {
          const endTime = performance.now();
          
          this.logAuthEvent({
            type: 'auth_request_failed',
            url: url,
            method: options.method || 'GET',
            duration: endTime - startTime,
            error: error.message
          });
          
          throw error;
        }
      }

      return originalFetch.apply(window, args);
    };
  }

  // API pública
  getMetrics() {
    return { ...this.metrics };
  }

  getReport() {
    return {
      timestamp: new Date().toISOString(),
      summary: {
        errors: this.metrics.errors.length,
        performance: this.getPerformanceSummary(),
        resources: this.getResourcesSummary(),
        userActivity: this.getUserActivitySummary()
      }
    };
  }

  // Método para adicionar métricas customizadas
  addCustomMetric(category, data) {
    if (!this.metrics[category]) {
      this.metrics[category] = [];
    }
    
    this.metrics[category].push({
      ...data,
      timestamp: new Date().toISOString()
    });
    
    this.trimMetrics(category);
  }
}

// Instanciar o monitor global
if (typeof window !== 'undefined') {
  window.productionMonitor = new ProductionMonitor();
}

export default ProductionMonitor;
